import { useEffect, useState } from 'react';
import { Button, Card } from '@plick/ui';
import { IconExternalLink, IconSpeakerphone, IconX } from '@tabler/icons-react';
import { Announcement } from '@interfaces/general';
import { plickTheme } from '@theme/index';
import { useSurveyShownOnce } from '@utils/analytics/useSurveyShownOnce';
import { AnnouncementOptionButtons } from '../shared/AnnouncementOptionButtons';
import { useRespondAnnouncement } from '../CommunicationsModal/hooks/useRespondAnnouncement';

interface Props {
  announcement: Announcement;
  /** Usuario clickeó la ✕ → descarte permanente (localStorage). */
  onDismiss: () => void;
  /** Usuario respondió una opción → ocultar solo en la sesión actual. */
  onRespond: () => void;
}

export const CommunicationsCard = ({ announcement, onDismiss, onRespond }: Props) => {
  // Instancia local del hook → isSubmitting solo afecta a esta tarjeta.
  const { respond, isSubmitting, submittingOptionId } = useRespondAnnouncement('card');

  // MEAS-1: card CON opciones renderizada → survey_shown (1 vez por comercio+encuesta).
  useSurveyShownOnce(announcement.uuid, 'card', Boolean(announcement.options?.length));

  const bgColor = announcement.bg_color || plickTheme.colors.light;
  const textColor = announcement.text_color || plickTheme.colors.dark;

  const initialDelay = announcement.delay_before_close ?? 0;
  const [canClose, setCanClose] = useState(initialDelay === 0);

  useEffect(() => {
    if (initialDelay > 0) {
      const t = setTimeout(() => setCanClose(true), initialDelay * 1000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [initialDelay]);

  // Persistente: la X solo aparece si ya respondió (mismo contrato que el banner).
  const isPersistent = announcement.is_persistent === true;
  const hasResponded = Boolean(announcement.user_response);
  const showCloseButton = (!isPersistent || hasResponded) && canClose;

  const hasMainUrl = announcement.url && !announcement.options?.length;

  // "Novedad" hasta que el comercio interactúe. Una vez que respondió, el
  // anuncio deja de ser nuevo aunque siga visible (is_persistent).
  const showNoveltyBadge = !hasResponded;

  // Tonos derivados del textColor para que el badge y el icono se vean
  // siempre legibles sobre cualquier bg_color configurado en el backend.
  // Usamos hex con alpha en lugar de bg-*/10 de Tailwind porque textColor es
  // dinámico (no podemos generar clases JIT en runtime).
  const accentSoft = `${textColor}1A`; // ~10% opacity
  const ringSoft = `${textColor}1F`; // ~12% opacity para el ring del wrapper

  return (
    <div
      className="rounded-xl shadow-xl"
      style={{
        // ring sutil pintado con boxShadow para tolerar cualquier bgColor
        // (no podemos usar ring-* de Tailwind con un color dinámico).
        boxShadow: `0 20px 25px -5px rgba(0,0,0,0.18), 0 8px 10px -6px rgba(0,0,0,0.10), 0 0 0 1px ${ringSoft}`,
      }}
    >
      <Card padding="0rem" backgroundColor={bgColor} borderRadius="0.75rem">
        <div
          style={{ color: textColor }}
          className="relative p-4 sm:p-5 flex flex-col gap-3 overflow-hidden rounded-xl"
        >
          {showCloseButton && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Descartar tarjeta"
              className="absolute top-2 right-2 p-1 rounded hover:opacity-80 transition-opacity"
              style={{ color: textColor }}
            >
              <IconX size={16} />
            </button>
          )}

          {showNoveltyBadge && (
            <div className="pr-6">
              <span
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: accentSoft, color: textColor }}
              >
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: textColor, opacity: 0.7 }}
                />
                Novedad
              </span>
            </div>
          )}

          <div className="flex items-start gap-3 pr-6 min-w-0">
            <span
              aria-hidden="true"
              className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full"
              style={{ backgroundColor: accentSoft, color: textColor }}
            >
              <IconSpeakerphone size={18} />
            </span>
            <div className="min-w-0">
              <h3 className="font-bold text-base sm:text-lg break-words leading-tight">{announcement.title}</h3>
              {announcement.subtitle && (
                <p className="text-sm opacity-90 mt-0.5 break-words">{announcement.subtitle}</p>
              )}
            </div>
          </div>

          {(announcement.text || announcement.long_description) && (
            <p className="text-sm opacity-80 whitespace-pre-wrap break-words">
              {announcement.text || announcement.long_description}
            </p>
          )}

          {announcement.options && announcement.options.length > 0 && (
            <AnnouncementOptionButtons
              options={announcement.options}
              announcementId={announcement.uuid}
              userResponse={announcement.user_response}
              bgColor={bgColor}
              textColor={textColor}
              isSubmitting={isSubmitting}
              submittingOptionId={submittingOptionId}
              isPersistent={announcement.is_persistent === true}
              variant="card"
              respond={respond}
              onComplete={onRespond}
            />
          )}

          {hasMainUrl && (
            <div className="self-start [&>button]:h-9 [&>button]:px-4 [&>button]:text-sm">
              <Button.Primary
                text="Más información"
                leftIcon={<IconExternalLink size={14} />}
                onClick={() => {
                  // onRespond despues del open por la misma razon que en Banner:
                  // un card is_persistent con url top-level y options=[] no
                  // tendria como cerrarse (la X esta oculta hasta hasResponded).
                  window.open(announcement.url ?? '#', '_blank', 'noopener,noreferrer');
                  onRespond();
                }}
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
